import gql from "graphql-tag";

// Single source of truth for the full product query. Shared by the product
// page (PDP) and the catalog card prefetch so a hover-prefetched result can be
// reused verbatim when the PDP opens.
export const PRODUCT_QUERY = gql`
  query ($id: String, $limit: Int) {
    product(id: $id) {
      id
      name
      shortDescription
      description
      model
      sku
      stock
      low_stock
      price
      special
      image
      videos
      reviews {
        author
        author_email
        content
        created_at
        rating
      }
      options {
        id
        name
        type
        values {
          id
          name
        }
      }
      imageLazy
      imageBig
      rating
      extra {
        name
        value
      }
      manufacturer {
        id
        name
        image
        imageLazy
        url(url: "/store/manufacturer/_id")
      }
      images(limit: $limit) {
        image
        imageLazy
        imageBig
      }
      attributes {
        name
        options
      }
      meta {
        title
        description
        keyword
      }
    }
  }
`;

// OpenCart's patched resolver accepts up to 100 gallery images. Request the
// complete product gallery instead of the old five-secondary-image cap (which
// silently limited every PDP to six slides including the hero). The gallery
// component controls network cost separately with tiny previews and a bounded
// full-resolution warm window, so returning the media metadata does not mean
// downloading every master image up front.
export const PRODUCT_GALLERY_IMAGE_LIMIT = 100;
export const PRODUCT_CACHE_MAX_ENTRIES = 32;
const TTL = 5 * 60 * 1000; // 5 minutes — keeps price/stock reasonably fresh

type Entry = { product: any; created: number };

// In-memory only (full product objects are large; we don't persist them to
// sessionStorage to avoid quota pressure). Lives for the browser session.
const mem = new Map<string, Entry>();
const inflight = new Map<string, Promise<any>>();

const sweepExpiredProducts = (now = Date.now()) => {
  for (const [key, entry] of mem) {
    if (now - entry.created > TTL) mem.delete(key);
  }
};

export function getCachedProduct(id: string | number): any | null {
  const key = String(id || "");
  if (!key) return null;
  const now = Date.now();
  sweepExpiredProducts(now);
  const entry = mem.get(key);
  if (!entry) return null;

  // Refresh LRU recency without extending the stock/price freshness TTL.
  mem.delete(key);
  mem.set(key, entry);
  return entry.product;
}

export function setCachedProduct(product: any): void {
  if (!product?.id) return;
  const now = Date.now();
  const key = String(product.id);
  sweepExpiredProducts(now);
  mem.delete(key);
  mem.set(key, { product, created: now });

  while (mem.size > PRODUCT_CACHE_MAX_ENTRIES) {
    const oldestKey = mem.keys().next().value;
    if (typeof oldestKey === "undefined") break;
    mem.delete(oldestKey);
  }
}

/**
 * Fetch the full product for `id` once and cache it. Concurrent callers (e.g.
 * a hover prefetch immediately followed by the PDP's own load) share a single
 * in-flight request. Resolves to the product object (or null on failure).
 *
 * Relies on the `apollo/query` action returning its own data (see
 * store/common/apollo) so this never reads the shared `apollo/get` getter.
 */
export function prefetchProduct(store: any, id: string | number): Promise<any> {
  const key = String(id || "");
  if (!key || typeof window === "undefined") return Promise.resolve(null);

  const cached = getCachedProduct(key);
  if (cached) return Promise.resolve(cached);

  const existing = inflight.get(key);
  if (existing) return existing;

  const request = store
    .dispatch("apollo/query", {
      query: PRODUCT_QUERY,
      variables: { id: key, limit: PRODUCT_GALLERY_IMAGE_LIMIT },
    })
    .then((data: any) => {
      const product = data?.product;
      if (product?.id) setCachedProduct(product);
      return product || null;
    })
    .catch(() => null)
    .finally(() => {
      inflight.delete(key);
    });

  inflight.set(key, request);
  return request;
}
